import type { ReactNode } from 'react'
import { Icon, type IconName } from '../ui/Icon'

/**
 * WHAT A SECTION OF THE RAIL IS, said by a glyph on its heading and a frame around
 * it — owner, 2026-10-12: Pinned 的這種標籤 … 使用某一種 icon 來代表, 比如說一個井字號,
 * 然後使用者的機器可以使用另外一種 icon 來代表, 讓使用者一目瞭然這就是他的機器. 把這些
 * 不同標籤、icon 以及機器要使用很明顯的這種卡片來把它分割出來, 這樣使用者就會知道自己在
 * 看什麼地方.
 *
 * Two kinds, because he named two (asked the same day whether Projects and Terminals
 * should be framed too: 標籤 + 機器). A section with no kind is drawn as it always was.
 * The chip is the launchpad's own (`.lp-chip`): blue is the lit pin's colour, green is
 * where agents live.
 */
const KINDS: Record<'tag' | 'machine', { icon: IconName; chip: string }> = {
  tag: { icon: 'tag', chip: 'lp-blue' },
  machine: { icon: 'computer', chip: 'lp-green' },
}

/**
 * ONE COLLAPSIBLE GROUP, the rail's own — a chevron, a label, a count, and a body that
 * opens and closes. Its open/closed state is the caller's (useSections keeps it across
 * reloads under `wt_sb_sections`).
 *
 * Lifted out of Sidebar.tsx so More can group by machine the SAME way the Agents rail
 * does — owner, 2026-09-29: More 的 panel 裡面, 把不同機器上面的 artifact 分類起來 …
 * 像在使用 Agents 這個 side panel 裡面可以讓它打開收起來的方式 … Backup 也可以使用同樣
 * 的方式, 依照機器分類把它收起來. One component, so the two places cannot drift into
 * two different-looking kinds of machine heading.
 */
export function SidebarSection({
  id,
  label,
  count,
  isOpen,
  onToggle,
  actions,
  kind,
  glyph,
  sub,
  children,
}: {
  id: string
  label: string
  count?: number | undefined
  isOpen: boolean
  onToggle: (id: string) => void
  /** Controls on the header's right — OUTSIDE the toggle (a button cannot nest
   *  a button): the machine sections' Terminal button rides here. */
  actions?: ReactNode
  /** A pin tag or a machine: its glyph on the heading, and the section in a frame
   *  of its own. Absent = a plain heading (Projects, Terminals, More's groups). */
  kind?: 'tag' | 'machine' | undefined
  /** Who the group is, drawn before its label where a kind's chip would be — an agent's face. */
  glyph?: ReactNode
  /** Said after the label, quieter: where the group is (the machine an agent is on). */
  sub?: string | undefined
  children: ReactNode
}) {
  const k = kind ? KINDS[kind] : null
  return (
    <div className={`sb-sec${kind ? ` sb-card sb-${kind}` : ''}`}>
      <div className="sb-sec-hdrow">
      <button
        type="button"
        className="sb-sec-hd"
        aria-expanded={isOpen}
        onClick={() => onToggle(id)}
      >
        <span className={`sb-chev${isOpen ? ' open' : ''}`} aria-hidden="true" />
        {k && (
          <span className={`sb-sec-ico lp-chip ${k.chip}`}>
            <Icon name={k.icon} size={14} />
          </span>
        )}
        {glyph && (
          <span className="sb-sec-glyph" aria-hidden="true">
            {glyph}
          </span>
        )}
        <span className="sb-sec-label">{label}</span>
        {sub && <span className="sb-sec-sub">· {sub}</span>}
        {count !== undefined && <span className="sb-sec-count">· {count}</span>}
      </button>
      {actions}
      </div>
      {isOpen && <div className="sb-sec-body">{children}</div>}
    </div>
  )
}
